import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** Loading lock so the same page is never requested or appended twice. */
  #isLoadingNext = false;

  /** Increases on every filter/sort change so stale loads are ignored. */
  #generation = 0;

  /** @type {number} */
  #loaderShownAt = 0;

  /** @type {number | undefined} */
  #loaderHideTimer;

  /** @type {IntersectionObserver | undefined} */
  #prefetchObserver;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#observeViewMore();
    this.#observePrefetch();
    this.addEventListener('click', this.#handleRetryClick);
    this.addEventListener('click', this.#rememberClickedCard);

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleRetryClick);
    this.removeEventListener('click', this.#rememberClickedCard);
    clearTimeout(this.#loaderHideTimer);
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#prefetchObserver?.disconnect();
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Prefetches the next/previous page only when the customer is about 1.5 screens away,
   * instead of immediately on page load, so it doesn't compete with the first images.
   */
  #observePrefetch() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.#prefetchObserver) {
      this.#prefetchObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const { viewMorePrevious, viewMoreNext } = this.refs;

            if (entry.target === viewMoreNext) {
              this.#fetchPage('next').catch(() => {});
            } else if (entry.target === viewMorePrevious) {
              this.#fetchPage('previous').catch(() => {});
            }
          }
        },
        { rootMargin: '150% 0px' }
      );
    }

    if (viewMorePrevious) this.#prefetchObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.#prefetchObserver.observe(viewMoreNext);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid || this.#isLoadingNext) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    this.#isLoadingNext = true;
    const generation = this.#generation;

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        this.#setLoadMoreState('loading');

        let failed = false;
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page. On failure, unblock the wait instead of hanging forever.
        this.#fetchPage('next').catch(() => {
          failed = !this.pages.has(nextPage.page);
          this.#resolveNextPagePromise?.();
          this.#resolveNextPagePromise = null;
        });

        await promise;

        // Filters or sort changed while this page was loading: drop it.
        if (generation !== this.#generation) return;

        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) {
          this.#setLoadMoreState(failed ? 'error' : 'idle', true);
          return;
        }
      }

      // De-duplicate by product ID (products can shift between pages if stock changes).
      const existingIds = new Set(
        Array.from(grid.querySelectorAll(':scope > [data-product-id]'), (el) => el.getAttribute('data-product-id'))
      );
      const newItems = Array.from(nextPageItemElements).filter(
        (el) => !existingIds.has(el.getAttribute('data-product-id'))
      );

      if (newItems.length) {
        grid.append(...newItems);
      } else {
        // Whole page was duplicates: move the page counter on so loading doesn't stall.
        const lastCard = /** @type {HTMLElement | null} */ (grid.querySelector(':scope > [data-page]:last-of-type'));
        if (lastCard) lastCard.dataset.page = String(nextPage.page);
      }

      this.#setLoadMoreState('idle');

      // Keep the indicator visible until the last page is in the grid
      const loadMoreStatus = this.querySelector('[data-load-more-status]');
      if (loadMoreStatus instanceof HTMLElement) {
        loadMoreStatus.dataset.more = nextPage.page < Number(grid.dataset.lastPage) ? 'true' : 'false';
      }

      this.#aspectRatioHelper?.processNewElements();

      await yieldToMainThread();

      history.pushState('', '', nextPage.url.toString());

      // The next page is now prefetched by #observePrefetch when the customer is ~1.5 screens from the end.
    } finally {
      if (generation === this.#generation) this.#isLoadingNext = false;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Shows or hides the infinite scroll loader.
   * The loader stays visible for at least 400ms so it never just flashes.
   * @param {'idle' | 'loading' | 'error'} state
   * @param {boolean} [immediate] - Skip the 400ms minimum
   */
  #setLoadMoreState(state, immediate = false) {
    const status = this.querySelector('[data-load-more-status]');
    if (!(status instanceof HTMLElement)) return;

    clearTimeout(this.#loaderHideTimer);

    if (state === 'idle' && !immediate && status.dataset.state === 'loading') {
      const remaining = 400 - (performance.now() - this.#loaderShownAt);
      if (remaining > 0) {
        this.#loaderHideTimer = window.setTimeout(() => this.#applyLoadMoreState('idle'), remaining);
        return;
      }
    }

    if (state === 'loading') this.#loaderShownAt = performance.now();
    this.#applyLoadMoreState(state);
  }

  /**
   * @param {'idle' | 'loading' | 'error'} state
   */
  #applyLoadMoreState(state) {
    const status = this.querySelector('[data-load-more-status]');
    if (!(status instanceof HTMLElement)) return;

    status.dataset.state = state;

    const message = status.querySelector('[data-load-more-message]');
    if (message) {
      message.textContent =
        state === 'loading'
          ? 'Loading more products'
          : state === 'error'
            ? "Couldn't load more products. Use the retry button."
            : '';
    }
  }

  /**
   * Retries loading the next page from the error link.
   * @param {MouseEvent} event
   */
  #handleRetryClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (!target?.closest('[data-load-more-retry]')) return;

    event.preventDefault();
    this.#renderNextPage();
  };

  /**
   * Before opening a product, saves the card's page and position in the URL,
   * so pressing Back reopens that page and scrolls to the same card.
   * @param {MouseEvent} event
   */
  #rememberClickedCard = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    const link = target?.closest('a[href]');
    if (!link) return;

    const card = /** @type {HTMLElement | null} */ (link.closest('[ref="cards[]"]'));
    if (!card || !card.id || !card.dataset.page) return;

    const url = new URL(window.location.href);
    if (card.dataset.page === '1') {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', card.dataset.page);
    }
    url.hash = card.id;
    history.replaceState(history.state, '', url.toString());
  };

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // Reset the loading lock and loader for the new filtered/sorted grid
    this.#generation += 1;
    this.#isLoadingNext = false;
    this.#setLoadMoreState('idle', true);

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();
        this.#observePrefetch();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}